import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Tabs, TabsList, TabsTrigger, TabsContent} from '@chekin/guestapp-ui-kit';
import {InstallationViaQrCode} from './InstallationViaQrCode';
import {InstallationManual} from './InstallationManual';
import {NetworkSetup} from './NetworkSetup';
import {Download} from 'lucide-react';
import type {InstallationInstructions, SelectOption} from '@chekin/core';
import type {TFunction} from 'i18next';
import {Select} from '@guestapp/old-ui-kit';

type OptionValue = {
  platform: 'ios' | 'android';
  index: number;
};

const hasMultipleAndroidInstructions = (instructions: InstallationInstructions) => {
  return instructions.android.length > 1;
};

const createOptions =
  (t: TFunction) =>
  (
    instructions: InstallationInstructions = {ios: [], android: [], language: ''},
  ): SelectOption<void, OptionValue, string>[] =>
    [
      ...instructions.ios.map((instruction, index) => ({
        label: t('esim.ios') + ` ${instruction.version || ''}`,
        value: {platform: 'ios' as const, index},
      })),
      ...instructions.android.map((_, index) => ({
        label: `${t('esim.android')} ${
          hasMultipleAndroidInstructions(instructions)
            ? t('option', {index: index + 1})
            : ''
        }`,
        value: {platform: 'android' as const, index},
      })),
    ];

type Props = {
  instructions: InstallationInstructions;
};
export const InstractionCard = ({instructions}: Props) => {
  const {t} = useTranslation();
  const options = createOptions(t)(instructions);
  const [selectedOption, setSelectedOption] = useState<
    SelectOption<void, OptionValue, string>
  >(options[0]);

  const instruction =
    instructions[selectedOption?.value.platform][selectedOption?.value.index];
  const directAppleInstallationUrl = instruction.direct_apple_installation_url;

  return (
    <div className="flex flex-col gap-6">
      <Select
        options={options}
        value={selectedOption || options[0]}
        onChange={option => option && setSelectedOption(option)}
      />
      {!!directAppleInstallationUrl && (
        <>
          <a
            href={directAppleInstallationUrl}
            target="_blank"
            className="flex flex-col gap-1 py-2 px-4 border border-[var(--primary)] rounded-lg"
          >
            <span className="flex items-center gap-2 text-[var(--primary)]">
              <Download size={20} className="shrink-0" />
              <span className="font-semibold">{t('esim.my.download_esim_profile')}</span>
            </span>
            <span className="text-xs">{t('esim.my.install_esim_directly')}</span>
          </a>
          <div className="text-sm">{t('esim.my.button_doesnt_work_text')}</div>
        </>
      )}
      <Tabs
        defaultValue="qr-code"
        className="w-full rounded-2xl border border-[hsl(var(--border))]"
      >
        <TabsList className="w-full py-0 px-4">
          <TabsTrigger value="qr-code">{t('esim.my.qr_code')}</TabsTrigger>
          <TabsTrigger value="manual">{t('esim.my.manual')}</TabsTrigger>
          <TabsTrigger value="networks">{t('esim.my.networks')}</TabsTrigger>
        </TabsList>
        <TabsContent value="qr-code" className="py-6 px-4 m-0">
          {instruction.installation_via_qr_code && (
            <InstallationViaQrCode details={instruction.installation_via_qr_code} />
          )}
        </TabsContent>
        <TabsContent value="manual" className="py-6 px-4 m-0">
          {instruction.installation_manual && (
            <InstallationManual details={instruction.installation_manual} />
          )}
        </TabsContent>
        <TabsContent value="networks" className="py-6 px-4 m-0">
          {instruction.network_setup && (
            <NetworkSetup details={instruction.network_setup} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};
